import { JUNCTION_SEGMENTS } from './definitions/junctions';
import { SEG_AD_GANTRY_40M } from './definitions/SEG_AD_GANTRY_40M';
import { SEG_COLLAPSE_40M } from './definitions/SEG_COLLAPSE_40M';
import { CURVE_SEGMENTS } from './definitions/SEG_CURVE';
import { SEG_INTERIOR_40M } from './definitions/SEG_INTERIOR_40M';
import { SEG_SHELL_60M } from './definitions/SEG_SHELL_60M';
import { SEG_TUNNEL_40M } from './definitions/SEG_TUNNEL_40M';
import { SEG_COMBO_40M } from './definitions/SEG_COMBO_40M';
import { SEG_CONTAINERS_40M } from './definitions/SEG_CONTAINERS_40M';
import { SEG_CONTAINER_TOWER_60M } from './definitions/SEG_CONTAINER_TOWER_60M';
import { SEG_CABLE_REEL_40M } from './definitions/SEG_CABLE_REEL_40M';
import { SEG_CABLE_REEL_RUN_60M } from './definitions/SEG_CABLE_REEL_RUN';
import { SEG_CONTAINER_TOWER_SHELL_60M } from './definitions/SEG_CONTAINER_TOWER_SHELL_60M';
import { SEG_CRANE_40M } from './definitions/SEG_CRANE_40M';
import { SEG_CRANE_SWING_20M } from './definitions/SEG_CRANE_SWING_20M';
import { SEG_CRANE_SWING_60M, SEG_CRANE_SWING_60M_TIGHT } from './definitions/SEG_CRANE_SWING_RUN';
import { BUS_SEGMENTS } from './definitions/SEG_BUS';
import { SEG_DOZER_60M } from './definitions/SEG_DOZER_60M';
import { SEG_EXCAVATOR_40M } from './definitions/SEG_EXCAVATOR_40M';
import { SEG_FORKLIFT_40M } from './definitions/SEG_FORKLIFT_40M';
import { SEG_OBSTACLE_TRUCK_40M } from './definitions/SEG_OBSTACLE_TRUCK_40M';
import { SEG_EARTHWORKS_40M } from './definitions/SEG_EARTHWORKS_40M';
import { SEG_GOLDEN_RAMP_60M } from './definitions/SEG_GOLDEN_RAMP_60M';
import { SEG_OBSTACLE_20M } from './definitions/SEG_OBSTACLE_20M';
import { SEG_PIT_LARGE_40M } from './definitions/SEG_PIT_LARGE';
import { PIT_SMALL_SEGMENTS } from './definitions/SEG_PIT_SMALL';
import { SEG_PIPES_40M } from './definitions/SEG_PIPES_40M';
import { SEG_RAMP_40M } from './definitions/SEG_RAMP_40M';
import { SEG_SCAFFOLD_40M } from './definitions/SEG_SCAFFOLD_40M';
import { SEG_STRAIGHT_20M } from './definitions/SEG_STRAIGHT_20M';
import { SEG_STRAIGHT_40M } from './definitions/SEG_STRAIGHT_40M';
import type { SegmentDefinition } from './SegmentDefinition';

/** Registry of all track modules. New modules are added to DEFAULT_SEGMENTS only. */
export class SegmentCatalog {
  private readonly byId = new Map<string, SegmentDefinition>();

  constructor(definitions: readonly SegmentDefinition[]) {
    for (const definition of definitions) {
      if (this.byId.has(definition.id)) throw new Error(`Duplicate segment id ${definition.id}`);
      this.byId.set(definition.id, definition);
    }
  }

  get(id: string): SegmentDefinition {
    const definition = this.byId.get(id);
    if (!definition) throw new Error(`Unknown segment id ${id}`);
    return definition;
  }

  all(): readonly SegmentDefinition[] {
    return [...this.byId.values()];
  }
}

/** Level variety phase 1: SEG_02/03 early, terrain set pieces (pit, scaffold, pipes, ramp) from 150–400 m. */
export const DEFAULT_SEGMENTS: readonly SegmentDefinition[] = [
  SEG_STRAIGHT_20M,
  ...JUNCTION_SEGMENTS,
  SEG_COLLAPSE_40M,
  SEG_STRAIGHT_40M,
  SEG_OBSTACLE_20M,
  ...PIT_SMALL_SEGMENTS,
  SEG_PIT_LARGE_40M,
  SEG_COMBO_40M,
  SEG_EARTHWORKS_40M,
  SEG_SCAFFOLD_40M,
  SEG_PIPES_40M,
  SEG_RAMP_40M,
  // Phase 2: moving hazards – crane loads (SEG_09), excavator (SEG_10), forklift (SEG_11), slow truck (SEG_03)
  SEG_CRANE_40M,
  // Swinging crane loads 2–4 in a row (chain rule), 20 m apart – zig-zag between them
  SEG_CRANE_SWING_20M,
  SEG_CRANE_SWING_60M,
  SEG_CRANE_SWING_60M_TIGHT,
  SEG_EXCAVATOR_40M,
  SEG_FORKLIFT_40M,
  SEG_OBSTACLE_TRUCK_40M,
  // Phase 3: smooth curves (SEG_15/16), enclosed modules (SEG_14 tunnel, SEG_13 interior, SEG_12 shell)
  ...CURVE_SEGMENTS,
  SEG_TUNNEL_40M,
  SEG_INTERIOR_40M,
  SEG_SHELL_60M,
  // Ad gantry over the road ≈ every 200 m, in place of plain straights (landmark rules, bookable banner)
  SEG_AD_GANTRY_40M,
  // Container row in the left lane – double jump onto the roofs (risk/reward route with bottles)
  SEG_CONTAINERS_40M,
  // Container tower: three containers, then a column of two stacked – two double jumps up, a beer crate on top in half the cases
  SEG_CONTAINER_TOWER_60M,
  // Big cable drum rolling towards the runner – straight down an outer lane or diagonally from the middle
  SEG_CABLE_REEL_40M,
  SEG_CABLE_REEL_RUN_60M,
  // The container tower against its end – the shell always follows: off the column onto its upper floor (drums up there)
  SEG_CONTAINER_TOWER_SHELL_60M,
  // Golden bottle after a ramp – every 500 m up to 2000 m, then every 1000 m (set-piece schedule), reached only with a double jump
  SEG_GOLDEN_RAMP_60M,
  // Dozer set piece – ≈ every 600 m from 700 m (set-piece schedule, gives way to the golden ramp): jump on, push earth into the pit
  SEG_DOZER_60M,
  // ructec-Bulli – once at 1000 m (set-piece schedule, highest priority): 1500 m drive through traffic over ramps, curves, crossings
  ...BUS_SEGMENTS,
];
